import { useGetGoogleMeeting } from "../../utils";
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    Divider,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useRecord } from "../../../../api/useRecord";
import { formatDateWithTime } from "../../../../utils/dateUtils";
import { useStore } from "../../../../store/store";

type SessionData<Technician> = {
    id: number;
    technician?: Technician;
    start_date?: string;
    end_date?: string;
};

type AppDialogSessionContentProps = {
    oldData: SessionData<{ id: number; user_id?: { first_name: string; last_name: string } }>;
    newData: SessionData<number>;
};

export const AppDialogSessionContent = ({ oldData, newData }: AppDialogSessionContentProps) => {
    const { appDialog } = useStore();

    const handleSubmit = (title: string) => {
        if (appDialog.onSubmit) appDialog.onSubmit(title);
        appDialog.toggleIsOpen();
    };

    const handleCancel = () => {
        if (appDialog.onCancel) appDialog.onCancel();
        appDialog.toggleIsOpen();
    };

    const { data: gMeetData, status: statusGMeet } = useGetGoogleMeeting(oldData.id);

    const { data: newTechnician, status } = useRecord(
        "technicians",
        "id,user_id.first_name,user_id.last_name",
        newData.technician
    );

    const [title, setTitle] = useState(gMeetData?.data?.title);

    useEffect(() => {
        if (!!gMeetData?.data?.title && !!oldData?.technician?.user_id && !!newTechnician?.item?.user_id) {
            const updatedTitle = updateTitle(
                gMeetData.data.title,
                makeUserCompleteName(oldData?.technician?.user_id),
                makeUserCompleteName(newTechnician?.item.user_id)
            );
            if (gMeetData.data.title !== updatedTitle) setTitle(updatedTitle);
        } else if (!!gMeetData?.data?.title && !title) {
            setTitle(gMeetData.data.title);
        }
    }, [
        gMeetData?.data?.title,
        oldData?.technician?.user_id?.first_name,
        oldData?.technician?.user_id?.last_name,
        newTechnician?.item?.user_id?.first_name,
        newTechnician?.item?.user_id?.last_name,
    ]);

    const isDifferentTechnician =
        !!oldData?.technician?.id && !!newData?.technician && oldData.technician.id !== newData.technician;
    const isDifferentStartDate =
        !!newData?.start_date &&
        datesAreSignificantlyDifferent(newData.start_date, gMeetData?.data?.start_date);
    const isDifferentEndDate =
        !!newData?.end_date && datesAreSignificantlyDifferent(newData.end_date, gMeetData?.data?.end_date);

    if (status === "loading" || statusGMeet === "loading") {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                overflow="hidden"
                mb={4}
            >
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <>
            <DialogContent>
                <Divider sx={{ mb: 2 }} />

                <Box display="flex" flexDirection="column" gap={4}>
                    <Box>
                        <Typography sx={{ fontWeight: "bold", color: "primary.dark" }} variant="subtitle2">
                            Título do convite
                        </Typography>
                        <TextField
                            size="small"
                            margin="dense"
                            InputLabelProps={{ sx: { fontSize: "0.9rem" } }}
                            variant="outlined"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Box>

                    <DisplayOldAndNewData
                        show={isDifferentTechnician}
                        title="Técnico por atualizar"
                        oldData={makeUserCompleteName(oldData?.technician?.user_id!)}
                        newData={makeUserCompleteName(newTechnician?.item?.user_id!)}
                    />
                    <DisplayOldAndNewData
                        show={isDifferentStartDate}
                        title="Data de início por atualizar"
                        oldData={formatDateWithTime(gMeetData?.data?.start_date)!}
                        newData={formatDateWithTime(newData?.start_date!)!}
                    />
                    <DisplayOldAndNewData
                        show={isDifferentEndDate}
                        title="Data de fim por atualizar"
                        oldData={formatDateWithTime(gMeetData?.data?.end_date)!}
                        newData={formatDateWithTime(newData?.end_date!)!}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: 4 }}>
                <Button onClick={handleCancel} color="secondary">
                    Cancelar
                </Button>
                <Button onClick={() => handleSubmit(title)} color="primary" variant="contained">
                    Enviar
                </Button>
            </DialogActions>
        </>
    );
};

const updateTitle = (title: string, oldTechnicianName: string, newTechnicianName: string) => {
    const splitedTitle = title.split(" ");
    const [newFirstName, newLastName] = newTechnicianName.split(" ");
    const [oldFirstName, oldLastName] = oldTechnicianName.split(" ");

    let matches = 0;
    const updatedTitle = splitedTitle.map((ele) => {
        if (ele === oldFirstName) {
            matches++;
            return newFirstName;
        }
        if (ele === oldLastName) {
            matches++;
            return newLastName;
        }
        return ele;
    });
    return matches === 2 ? updatedTitle.join(" ") : title;
};

const makeUserCompleteName = (user_id: { first_name: string; last_name: string }) => {
    if (user_id?.first_name && user_id.last_name) {
        return `${user_id.first_name} ${user_id.last_name}`;
    }
    return "adicionar manualmente";
};

const datesAreSignificantlyDifferent = (dataA: string, dataB: string) => {
    const diffInMs = Math.abs(new Date(dataA).getTime() - new Date(dataB).getTime());
    const isSignificantlyDifferent = diffInMs >= 10000;
    return isSignificantlyDifferent;
};

type DisplayOldAndNewDataProps = {
    show: boolean;
    title: string;
    oldData: string;
    newData: string;
};
const DisplayOldAndNewData = ({ show, title, oldData, newData }: DisplayOldAndNewDataProps) => {
    if (!show) return null;
    return (
        <Box>
            <Typography sx={{ fontWeight: "bold", color: "primary.dark" }} variant="subtitle2">
                {title}
            </Typography>
            <Box display="flex" flexDirection="row" gap={4}>
                <Typography color={"error"}>{oldData}</Typography>
                -&gt;
                <Typography color={"primary"}>{newData}</Typography>
            </Box>
        </Box>
    );
};
